// components/ParticlesBackground.js
import { useEffect, useState } from "react";

export default function ParticlesBackground() {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [shouldRenderParticles, setShouldRenderParticles] = useState(true);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isMobileDevice = width <= 768;
      const isTabletDevice = width > 768 && width <= 1024;

      setIsMobile(isMobileDevice);
      setIsTablet(isTabletDevice);

      // Enable particles on all screens, but with different configurations
      setShouldRenderParticles(true);
    };

    // Check on mount
    checkDevice();

    // Check on resize
    window.addEventListener('resize', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
      // Cleanup particles instance
      if (window.pJSDom && window.pJSDom.length > 0) {
        // Traverse and destroy all instances to be safe, or just the relevant one
        // Simple approach: clear the global array if we are unmounting the single background
        // But properly: call destroy
        try {
          window.pJSDom.forEach(pJS => {
            if (pJS.pJS.fn.vendors.destroypJS) {
              pJS.pJS.fn.vendors.destroypJS();
            }
          });
          window.pJSDom = [];
        } catch (e) {
          console.warn("Particles cleanup error", e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldRenderParticles || !window.particlesJS) return;

    // Create optimized config based on device type
    const getOptimizedConfig = () => {
      if (isMobile) {
        return {
          particles: {
            number: {
              value: 25, // Increased for better visibility
              density: {
                enable: true,
                value_area: 800
              }
            },
            color: {
              value: "#ffffff"
            },
            shape: {
              type: "circle"
            },
            opacity: {
              value: 0.2, // Increased opacity for better visibility
              random: false
            },
            size: {
              value: 2, // Larger particles for better visibility
              random: false
            },
            line_linked: {
              enable: false
            },
            move: {
              enable: true,
              speed: 0.8, // Slower movement
              direction: "none",
              random: true,
              straight: false,
              out_mode: "out",
              bounce: false
            }
          },
          interactivity: {
            events: {
              onhover: { enable: false },
              onclick: { enable: false },
              resize: true
            }
          },
          retina_detect: false // Disable retina detection on mobile
        };
      } else if (isTablet) {
        return {
          particles: {
            number: {
              value: 30, // Medium count
              density: {
                enable: true,
                value_area: 1000
              }
            },
            color: {
              value: "#ffffff"
            },
            shape: {
              type: "circle"
            },
            opacity: {
              value: 0.3,
              random: false
            },
            size: {
              value: 2,
              random: false
            },
            line_linked: {
              enable: false
            },
            move: {
              enable: true,
              speed: 1.2,
              direction: "none",
              random: true,
              straight: false,
              out_mode: "out",
              bounce: false
            }
          },
          interactivity: {
            events: {
              onhover: { enable: false },
              onclick: { enable: false },
              resize: true
            }
          },
          retina_detect: true
        };
      } else {
        // Desktop - use original config
        return {
          particles: {
            number: {
              value: 25,
              density: {
                enable: true,
                value_area: 1200
              }
            },
            color: {
              value: "#ffffff"
            },
            shape: {
              type: "circle"
            },
            opacity: {
              value: 0.19,
              random: false
            },
            size: {
              value: 2,
              random: false
            },
            line_linked: {
              enable: false
            },
            move: {
              enable: true,
              speed: 1.6,
              direction: "none",
              random: true,
              straight: false,
              out_mode: "out",
              bounce: false
            }
          },
          interactivity: {
            events: {
              onhover: { enable: false },
              onclick: { enable: false },
              resize: true
            }
          },
          retina_detect: true
        };
      }
    };

    const config = getOptimizedConfig();

    window.particlesJS("particles-js", config);
  }, [shouldRenderParticles, isMobile, isTablet]);

  // Don't render particles container on very small screens
  if (!shouldRenderParticles) {
    return null;
  }

  return (
    <div
      id="particles-js"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        // Add will-change for better performance
        willChange: "transform",
        // Allow interactions to pass through to elements behind
        pointerEvents: "none"
      }}
    />
  );
}
